/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Página do produto: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Programado por www.creative-tim.com

 =========================================================

* O aviso de copyright acima e este aviso de permissão
* devem ser incluídos em todas as cópias ou partes substanciais do Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { Link, Navigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import FloatSemanticMessage from "components/FloatSemanticMessage";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";
import {
  clearPendingGoogleCredential,
  getGoogleCredentialFromError,
  linkPendingGoogleCredentialToUser,
  signInWithEmailAndPasswordFirebase,
  signInWithGoogleFirebase,
  setAuthPersistence,
  storePendingGoogleCredential,
  subscribeToAuthChanges,
} from "services/firebase";
import { syncAuthenticatedUser } from "services/users";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import googleLogo from "assets/images/logos/google.png";

function Basic() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingEmailSignIn, setIsLoadingEmailSignIn] = useState(false);
  const [isLoadingGoogleSignIn, setIsLoadingGoogleSignIn] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState(undefined);

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((user) => {
      setAuthenticatedUser(user);
    });

    return unsubscribe;
  }, []);

  const resolveAuthErrorMessage = (error) => {
    const authCode = error?.code;

    switch (authCode) {
      case "auth/invalid-email":
        return "O e-mail informado e invalido.";
      case "auth/user-disabled":
        return "Esta conta foi desativada.";
      case "auth/user-not-found":
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return "E-mail ou senha incorretos.";
      case "auth/popup-closed-by-user":
        return "Login com Google cancelado.";
      case "auth/popup-blocked":
        return "O navegador bloqueou o popup do Google. Libere popups e tente novamente.";
      case "auth/account-exists-with-different-credential":
        return "Este e-mail ja possui cadastro com outro metodo de acesso.";
      default:
        return error?.message || "Nao foi possivel autenticar agora. Tente novamente.";
    }
  };

  const validateFields = () => {
    if (!email.trim()) {
      setErrorMessage("Informe seu e-mail.");
      return false;
    }

    if (!password) {
      setErrorMessage("Informe sua senha.");
      return false;
    }

    return true;
  };

  const handleEmailPasswordSignIn = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (!validateFields()) return;

    setIsLoadingEmailSignIn(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      await setAuthPersistence();
      const userCredential = await signInWithEmailAndPasswordFirebase(normalizedEmail, password);
      await linkPendingGoogleCredentialToUser(userCredential.user);
      await syncAuthenticatedUser(userCredential.user, { email: normalizedEmail });
    } catch (error) {
      setErrorMessage(resolveAuthErrorMessage(error));
    } finally {
      setIsLoadingEmailSignIn(false);
    }
  };

  const handleGoogleSignIn = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setIsLoadingGoogleSignIn(true);

    try {
      await setAuthPersistence();
      const userCredential = await signInWithGoogleFirebase();
      await syncAuthenticatedUser(userCredential.user);
      clearPendingGoogleCredential();
    } catch (error) {
      if (error?.code === "auth/account-exists-with-different-credential") {
        const conflictingEmail = error?.customData?.email;
        const pendingGoogleCredential = getGoogleCredentialFromError(error);

        if (pendingGoogleCredential) {
          storePendingGoogleCredential(pendingGoogleCredential);
        }

        setEmail(conflictingEmail || "");
        setErrorMessage(
          "Este e-mail já usa outra forma de acesso. Entre com a senha da conta para vincular o Google."
        );
      } else {
        setErrorMessage(resolveAuthErrorMessage(error));
      }
    } finally {
      setIsLoadingGoogleSignIn(false);
    }
  };

  if (authenticatedUser && !isLoadingEmailSignIn && !isLoadingGoogleSignIn && !errorMessage) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <FloatSemanticMessage
        open={Boolean(errorMessage)}
        title="Falha no login"
        message={errorMessage || ""}
        severity="error"
        onClose={() => setErrorMessage("")}
      />
      <BasicLayout image={bgImage}>
        <Card>
          <MDBox
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            mx={2}
            mt={-3}
            p={2}
            mb={1}
            textAlign="center"
          >
            <MDTypography variant="h4" fontWeight="medium" color="white" mt={6} mb={6}>
              Entrar
            </MDTypography>
          </MDBox>

          <MDBox pt={4} pb={3} px={3}>
            <MDBox component="form" role="form" onSubmit={handleEmailPasswordSignIn}>
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label="E-mail"
                  fullWidth
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </MDBox>

              <MDBox mb={2}>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  label="Senha"
                  fullWidth
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          <Icon>{showPassword ? "visibility_off" : "visibility"}</Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>

              <MDBox mt={4} mb={1}>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={isLoadingEmailSignIn || isLoadingGoogleSignIn}
                >
                  {isLoadingEmailSignIn ? <CircularProgress size={18} color="inherit" /> : "Entrar"}
                </MDButton>
              </MDBox>
              <MDBox mb={1}>
                <MDButton
                  variant="outlined"
                  color="info"
                  fullWidth
                  onClick={handleGoogleSignIn}
                  disabled={isLoadingEmailSignIn || isLoadingGoogleSignIn}
                  startIcon={
                    !isLoadingGoogleSignIn ? (
                      <MDBox
                        component="img"
                        src={googleLogo}
                        alt="Google"
                        width="1rem"
                        height="1rem"
                      />
                    ) : null
                  }
                >
                  {isLoadingGoogleSignIn ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Entrar com Google"
                  )}
                </MDButton>
              </MDBox>

              <MDBox mt={3} mb={1} textAlign="center">
                <MDTypography variant="button" color="text">
                  Não tem uma conta?{" "}
                  <MDTypography
                    component={Link}
                    to="/signup"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    Cadastre-se
                  </MDTypography>
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </BasicLayout>
    </>
  );
}

export default Basic;
